<template>
  <messenger-view-layout
    :title="channelTitle"
    :channel-type="channelType"
    @back="$emit('back')"
    @resize="$emit('resize', $event)"
  >
    <div
      class="view-content"
      :class="{
        'view-content--show-qr-code': showQrCode,
      }"
    >
      <div
        v-if="contactData"
        class="mb-4"
      >
        <messenger-view-content-header
          class="mb-5"
          :title="contentTitle"
          :description="contentDescription"
        />
        <div
          v-if="phoneNumberFormatted"
          class="emphasize--text hero-24 text-center"
        >
          {{ phoneNumberFormatted }}
        </div>
      </div>
      <slot
        v-else
        name="not-exists-contact-data"
      />

      <div class="main-view-content">
        <div>
          <widget-button
            v-bind="themeButtonProps"
            class="w100pr"
            :is-disabled="!contactData?.href"
            @click="contact"
          >
            <messenger-text-with-emoji :text="buttonText" />
          </widget-button>
          <div
            v-if="contactWarningText || showQrCode"
            class="contact-button-description mt-2"
          >
            {{ contactWarningText }}
          </div>
        </div>

        <div
          v-if="showQrCode"
          class="view-content__qr-block qr-block mt-6"
        >
          <div
            class="qr-block__qr"
            :class="{ 'qr-block__qr--empty': !qrCode }"
          >
            <img
              :src="qrCode || qrCodeStubImageUrl"
              alt="qrcode"
            />
          </div>
          <div class="qr-block__description">
            <span v-if="qrCode">{{ scanQrText }}</span>
            <span v-else>{{ $t('messengerWidget.qrCodeWillBeDisplayedHere') }}</span>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:alert>
      <slot name="alert" />
    </template>
  </messenger-view-layout>
</template>

<script setup lang="ts">
import type { QrCodeContactData } from '@widgets/messenger/types'
import MessengerViewLayout from '@widgets/messenger/components/MessengerViewLayout.vue'
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import { phoneService } from '@widgets/shared/services/phoneService'
import { qrCodeService } from '@widgets/shared/services/qrCodeService'
import MessengerViewContentHeader from '@widgets/messenger/components/MessengerViewContentHeader.vue'
import MessengerTextWithEmoji from '@widgets/messenger/components/MessengerTextWithEmoji.vue'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import { sendOpenLinkMessage } from '@widgets/messenger/utils/parentLinkMessages'
import { useWidgetStore } from '@widgets/messenger/stores/useWidgetStore'
import { computed, ref, watch } from '@/composition/vue/compositionApi'
import qrCodeStubImageUrl from '@/assets/images/qr-code-stub.svg'
import type { WebWidgetChannelType } from '@/data/models/domain/messages/settings/webWidget/types'

const {
  contactData,
  qrCodeLogoUrl,
  openHrefInNewWindow,
  showQrCode = true,
} = defineProps<{
  channelTitle: string
  channelType: WebWidgetChannelType
  contentTitle: string
  contentDescription: string
  buttonText: string
  scanQrText: string
  contactData?: QrCodeContactData
  contactWarningText?: string
  openHrefInNewWindow?: boolean
  qrCodeLogoUrl?: string
  showQrCode?: boolean
}>()

defineEmits<{
  (e: 'back'): void
  (e: 'resize', height: number): void
}>()

const { themeButtonProps } = useWidgetTheme()
const widgetStore = useWidgetStore()

const phoneNumberFormatted = computed(() => {
  return contactData?.phoneNumber
    ? phoneService.format(contactData.phoneNumber.phone, contactData.phoneNumber.countryId)
    : ''
})

const qrCodeSize = 140
const retinaMultiplier = window.devicePixelRatio || 1
const qrCode = ref('')
watch(
  () => contactData?.href,
  async (value) => {
    qrCode.value = value
      ? await qrCodeService.getBase64(value, {
          margin: 0,
          size: qrCodeSize * retinaMultiplier,
          ...(qrCodeLogoUrl
            ? {
                logo: {
                  src: qrCodeLogoUrl,
                  width: 44 * retinaMultiplier,
                  height: 44 * retinaMultiplier,
                },
              }
            : {}),
        })
      : ''
  },
  { immediate: true },
)

const contact = () => {
  if (!contactData?.href) return
  // Send message to parent window to open the link - fixes iOS 18.5 iframe restrictions.
  // window.open() and window.location.href are blocked by Safari/Chrome on iOS 18.5 for tel:, sms: and other app schemes.
  // <a href="tel:..."></a> also not working in iframe.
  sendOpenLinkMessage(widgetStore.uniqueCode.value, contactData.href, openHrefInNewWindow || false)
}
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

$qrCodeSize: calc(v-bind(qrCodeSize) * 1px);
$qrCodeContainerSize: calc(v-bind(qrCodeSize) * 1px + 40px);

.view-content {
  padding: 40px 24px 8px;
  text-align: center;

  &--show-qr-code {
    padding-bottom: 38px;
  }
}

.main-view-content {
  &--disabled {
    opacity: 0.4;
    pointer-events: none;
  }
}

.contact-button-description {
  color: $distinct;
  font-size: 12px;
  line-height: 16px;
  min-height: 16px * 2;
}

.qr-block {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__description {
    color: $distinct;
    font-size: 12px;
    line-height: 16px;
    margin-top: 16px;
  }

  &__qr {
    width: $qrCodeContainerSize;
    height: $qrCodeContainerSize;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: $qrCodeSize;
      height: $qrCodeSize;
    }

    &--empty {
      background-color: $shade;
      border-radius: $border-radius-md;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 66px;
        height: 66px;
      }
    }

    &:not(&--empty) {
      background-image: url('@/assets/images/widget/qr-corners.svg');
      background-position: center;
      background-size: contain;
    }
  }
}
</style>
